import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Settings,
  Bell,
  Mail,
  Save,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Info,
  Plus,
  X,
  Shield,
  FileText,
  LayoutDashboard,
  Globe,
  Gauge,
  Eye,
  EyeOff,
  GripVertical,
  RefreshCw,
  Check,
  Edit2,
  Trash2,
  Database,
} from "lucide-react";
import { toast } from "sonner";

// Adapters Settings Component (Rubicon/Magnite credentials)
function AdaptersSettings({ api, isAdmin }) {
  const [rubicon, setRubicon] = useState({ username: "", password: "" });
  const [loadingR, setLoadingR] = useState(true);
  const [savingR, setSavingR] = useState(false);

  useEffect(() => {
    api.get("/adapter-credentials/rubicon").then(r => {
      setRubicon({ username: r.data.username || "", password: r.data.password || "" });
    }).catch(() => {}).finally(() => setLoadingR(false));
  }, [api]);

  const saveRubicon = async () => {
    setSavingR(true);
    try {
      await api.put("/adapter-credentials/rubicon", rubicon);
      toast.success("Rubicon credentials saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingR(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Adapter Credentials
        </CardTitle>
        <CardDescription>
          Configure authentication credentials for third-party adapters
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Rubicon / Magnite */}
        <div className="p-4 border rounded-lg space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-blue-600 border-blue-300">Rubicon / Magnite</Badge>
          </div>
          {loadingR ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    value={rubicon.username}
                    onChange={e => setRubicon({ ...rubicon, username: e.target.value })}
                    placeholder="Rubicon username"
                    data-testid="rubicon-username"
                    disabled={!isAdmin}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={rubicon.password}
                    onChange={e => setRubicon({ ...rubicon, password: e.target.value })}
                    placeholder="Rubicon password"
                    data-testid="rubicon-password"
                    disabled={!isAdmin}
                  />
                </div>
              </div>
              {isAdmin && (
                <Button onClick={saveRubicon} disabled={savingR} size="sm" data-testid="save-rubicon-btn">
                  {savingR ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><Save className="mr-2 h-4 w-4" />Save Credentials</>}
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Permission Set Editor Component
function PermissionSetEditor({ permissionSet, availablePermissions, onSave, onCancel }) {
  const [name, setName] = useState(permissionSet?.name || "");
  const [description, setDescription] = useState(permissionSet?.description || "");
  const [selectedPermissions, setSelectedPermissions] = useState(
    permissionSet?.permissions || []
  );

  const togglePermission = (permission) => {
    if (selectedPermissions.includes(permission)) {
      setSelectedPermissions(selectedPermissions.filter((p) => p !== permission));
    } else {
      setSelectedPermissions([...selectedPermissions, permission]);
    }
  };

  const toggleCategory = (category, permissions) => {
    const allSelected = permissions.every((p) => selectedPermissions.includes(p));
    if (allSelected) {
      setSelectedPermissions(selectedPermissions.filter((p) => !permissions.includes(p)));
    } else {
      setSelectedPermissions([...new Set([...selectedPermissions, ...permissions])]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        <div>
          <Label>Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Permission set name"
          />
        </div>
        <div>
          <Label>Description</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this permission set"
          />
        </div>
      </div>

      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {Object.entries(availablePermissions?.categories || {}).map(([category, permissions]) => (
          <div key={category} className="border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-semibold">{category}</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleCategory(category, permissions)}
              >
                {permissions.every((p) => selectedPermissions.includes(p)) ? "Deselect All" : "Select All"}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {permissions.map((permission) => (
                <div
                  key={permission}
                  className="flex items-center space-x-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                  onClick={() => togglePermission(permission)}
                >
                  <Checkbox checked={selectedPermissions.includes(permission)} />
                  <span className="text-sm">{permission.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSave({ name, description, permissions: selectedPermissions })}>
          Save Permission Set
        </Button>
      </div>
    </div>
  );
}

// Email Template Editor Component
function EmailTemplateEditor({ template, onSave, onCancel, onPreview }) {
  const [subject, setSubject] = useState(template?.subject || "");
  const [htmlTemplate, setHtmlTemplate] = useState(template?.html_template || "");

  return (
    <div className="space-y-4">
      <div>
        <Label>Subject</Label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email subject"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Available variables: {template?.variables?.map((v) => `{{${v}}}`).join(", ")}
        </p>
      </div>

      <div>
        <Label>HTML Template</Label>
        <Textarea
          value={htmlTemplate}
          onChange={(e) => setHtmlTemplate(e.target.value)}
          placeholder="HTML template content"
          className="font-mono text-sm min-h-[300px]"
        />
      </div>

      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => onPreview({ subject, html_template: htmlTemplate })}
        >
          <Eye className="mr-2 h-4 w-4" />
          Preview
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onSave({ subject, html_template: htmlTemplate })}>
            Save Template
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPageAdvanced() {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState("notifications");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: false,
    recipients: [],
    alert_severities: ["critical", "high"],
  });
  const [newEmail, setNewEmail] = useState("");

  // Permission sets
  const [permissionSets, setPermissionSets] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [editingPermissionSet, setEditingPermissionSet] = useState(null);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);

  // Email templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  // Dashboard widgets
  const [dashboardLayout, setDashboardLayout] = useState({ widgets: [] });

  // Language
  const [languages, setLanguages] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState("en");

  // Rate limits
  const [rateLimits, setRateLimits] = useState({ roles: {}, endpoints: {} });

  // Platform settings
  const [platformSettings, setPlatformSettings] = useState({ store_bid_logs: true, store_audience: true });

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const [notifResp, permResp, availPermResp, templatesResp, dashboardResp, langResp, rateResp, platformResp] = await Promise.all([
        api.get("/notification-settings/alerts").catch(() => ({ data: notificationSettings })),
        api.get("/settings/permission-sets").catch(() => ({ data: [] })),
        api.get("/settings/permissions/available").catch(() => ({ data: { categories: {}, all_permissions: [] } })),
        api.get("/settings/email-templates").catch(() => ({ data: [] })),
        api.get("/settings/dashboard/layout").catch(() => ({ data: { widgets: [] } })),
        api.get("/settings/languages").catch(() => ({ data: { languages: { en: "English" } } })),
        api.get("/settings/rate-limits").catch(() => ({ data: { roles: {}, endpoints: {} } })),
        api.get("/settings/platform").catch(() => ({ data: { store_bid_logs: true, store_audience: true } })),
      ]);

      setNotificationSettings(notifResp.data);
      setPermissionSets(permResp.data);
      setAvailablePermissions(availPermResp.data);
      setEmailTemplates(templatesResp.data);
      setDashboardLayout(dashboardResp.data);
      setLanguages(langResp.data.languages);
      setRateLimits(rateResp.data);
      setPlatformSettings(platformResp.data);
      setCurrentLanguage(user?.language || "en");
    } catch (error) {
      console.error("Failed to load settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Notification handlers
  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      await api.put("/notification-settings/alerts", notificationSettings);
      toast.success("Notification settings saved");
    } catch (error) {
      toast.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (notificationSettings.recipients.includes(newEmail)) {
      toast.error("Email already added");
      return;
    }
    setNotificationSettings({
      ...notificationSettings,
      recipients: [...notificationSettings.recipients, newEmail],
    });
    setNewEmail("");
  };

  // Permission set handlers
  const savePermissionSet = async (data) => {
    try {
      if (editingPermissionSet?.id && !editingPermissionSet.is_system) {
        await api.put(`/settings/permission-sets/${editingPermissionSet.id}`, data);
        toast.success("Permission set updated");
      } else {
        await api.post("/settings/permission-sets", data);
        toast.success("Permission set created");
      }
      fetchAllSettings();
      setShowPermissionDialog(false);
      setEditingPermissionSet(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save permission set");
    }
  };

  const deletePermissionSet = async (id) => {
    try {
      await api.delete(`/settings/permission-sets/${id}`);
      toast.success("Permission set deleted");
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete permission set");
    }
  };

  // Template handlers
  const saveEmailTemplate = async (data) => {
    try {
      await api.put(`/settings/email-templates/${editingTemplate.id}`, data);
      toast.success("Template saved");
      fetchAllSettings();
      setShowTemplateDialog(false);
      setEditingTemplate(null);
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  const resetEmailTemplate = async (templateId) => {
    try {
      await api.post(`/settings/email-templates/${templateId}/reset`);
      toast.success("Template reset to default");
      fetchAllSettings();
    } catch (error) {
      toast.error("Failed to reset template");
    }
  };

  const previewEmailTemplate = async (data) => {
    try {
      const response = await api.post("/settings/email-templates/preview", {
        template_id: editingTemplate.id,
        variables: editingTemplate.variables.reduce((acc, v) => {
          acc[v] = `[Sample ${v}]`;
          return acc;
        }, {}),
      });
      setPreviewHtml(response.data.html);
    } catch (error) {
      toast.error("Failed to preview template");
    }
  };

  // Dashboard layout handlers
  const toggleWidgetVisibility = (widgetId) => {
    const updatedWidgets = dashboardLayout.widgets.map((w) =>
      w.id === widgetId ? { ...w, visible: !w.visible } : w
    );
    setDashboardLayout({ ...dashboardLayout, widgets: updatedWidgets });
  };

  const saveDashboardLayout = async () => {
    try {
      await api.put("/settings/dashboard/layout", {
        widgets: dashboardLayout.widgets.map((w, i) => ({
          id: w.id,
          visible: w.visible,
          order: i,
          size: w.size,
        })),
      });
      toast.success("Dashboard layout saved");
    } catch (error) {
      toast.error("Failed to save dashboard layout");
    }
  };

  const resetDashboardLayout = async () => {
    try {
      await api.post("/settings/dashboard/layout/reset");
      toast.success("Dashboard layout reset");
      fetchAllSettings();
    } catch (error) {
      toast.error("Failed to reset dashboard layout");
    }
  };

  // Get the i18n context for language changes
  const { changeLanguage: i18nChangeLanguage } = useI18n();

  // Language handler
  const changeLanguage = async (lang) => {
    try {
      await api.put("/settings/user/language", { language: lang });
      setCurrentLanguage(lang);
      // Also update the I18nContext so translations are reloaded
      await i18nChangeLanguage(lang);
      toast.success("Language preference saved");
    } catch (error) {
      toast.error("Failed to save language preference");
    }
  };

  // Rate limit handler
  const updateRateLimit = async (role, config) => {
    try {
      await api.put(`/settings/rate-limits/role/${role}`, null, { params: config });
      toast.success(`Rate limit for ${role} updated`);
      fetchAllSettings();
    } catch (error) {
      toast.error("Failed to update rate limit");
    }
  };

  const severityOptions = [
    { value: "critical", label: "Critical", color: "text-red-500" },
    { value: "high", label: "High", color: "text-orange-500" },
    { value: "medium", label: "Medium", color: "text-amber-500" },
    { value: "low", label: "Low", color: "text-blue-500" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page-advanced">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Settings className="h-8 w-8 text-accent" />
            Settings
          </h1>
          <p className="text-muted-foreground">
            Configure system preferences, permissions, and customizations
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-3 md:grid-cols-6 gap-2">
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden md:inline">Notifications</span>
          </TabsTrigger>
          {isSuperAdmin && (
          <TabsTrigger value="permissions" className="gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden md:inline">Permissions</span>
          </TabsTrigger>
          )}
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden md:inline">Templates</span>
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="gap-2">
            <LayoutDashboard className="h-4 w-4" />
            <span className="hidden md:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger value="language" className="gap-2">
            <Globe className="h-4 w-4" />
            <span className="hidden md:inline">Language</span>
          </TabsTrigger>
          <TabsTrigger value="rate-limits" className="gap-2">
            <Gauge className="h-4 w-4" />
            <span className="hidden md:inline">Rate Limits</span>
          </TabsTrigger>
          <TabsTrigger value="adapters" className="gap-2">
            <Settings className="h-4 w-4" />
            <span className="hidden md:inline">Adapters</span>
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="platform" className="gap-2">
              <Database className="h-4 w-4" />
              <span className="hidden md:inline">Platform</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Alert Notifications
              </CardTitle>
              <CardDescription>
                Configure email notifications for bid pattern alerts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base">Enable Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Send email alerts when anomalies are detected
                  </p>
                </div>
                <Switch
                  checked={notificationSettings.enabled}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, enabled: checked })
                  }
                  disabled={!isAdmin}
                />
              </div>

              <div className="space-y-3">
                <Label>Email Recipients</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email address"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addRecipient()}
                    disabled={!isAdmin}
                  />
                  <Button onClick={addRecipient} disabled={!isAdmin}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {notificationSettings.recipients.map((email) => (
                    <Badge key={email} variant="secondary" className="gap-1">
                      <Mail className="h-3 w-3" />
                      {email}
                      {isAdmin && (
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-destructive"
                          onClick={() =>
                            setNotificationSettings({
                              ...notificationSettings,
                              recipients: notificationSettings.recipients.filter((e) => e !== email),
                            })
                          }
                        />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Alert Severities</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {severityOptions.map(({ value, label, color }) => (
                    <div
                      key={value}
                      className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer ${
                        notificationSettings.alert_severities.includes(value)
                          ? "border-accent bg-accent/5"
                          : ""
                      }`}
                      onClick={() => {
                        if (!isAdmin) return;
                        const severities = notificationSettings.alert_severities.includes(value)
                          ? notificationSettings.alert_severities.filter((s) => s !== value)
                          : [...notificationSettings.alert_severities, value];
                        setNotificationSettings({ ...notificationSettings, alert_severities: severities });
                      }}
                    >
                      <Checkbox
                        checked={notificationSettings.alert_severities.includes(value)}
                        disabled={!isAdmin}
                      />
                      <span className={color}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {isAdmin && (
                <Button onClick={saveNotificationSettings} disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save Changes
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Permission Sets
                </CardTitle>
                <CardDescription>
                  Manage custom permission sets for fine-grained access control
                </CardDescription>
              </div>
              {isAdmin && (
                <Button
                  onClick={() => {
                    setEditingPermissionSet(null);
                    setShowPermissionDialog(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Set
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permissionSets.map((set) => (
                    <TableRow key={set.id}>
                      <TableCell className="font-medium">{set.name}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[200px] truncate">
                        {set.description}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{set.permissions?.length || 0} permissions</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={set.is_system ? "outline" : "default"}>
                          {set.is_system ? "System" : "Custom"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingPermissionSet(set);
                              setShowPermissionDialog(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!set.is_system && isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deletePermissionSet(set.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Email Templates
              </CardTitle>
              <CardDescription>
                Customize email templates for notifications and alerts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Variables</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[200px] truncate">
                        {template.description}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {template.variables?.slice(0, 3).map((v) => (
                            <Badge key={v} variant="outline" className="text-xs">
                              {v}
                            </Badge>
                          ))}
                          {template.variables?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{template.variables.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {isAdmin && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setEditingTemplate(template);
                                  setShowTemplateDialog(true);
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => resetEmailTemplate(template.id)}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <LayoutDashboard className="h-5 w-5" />
                  Dashboard Widgets
                </CardTitle>
                <CardDescription>
                  Customize which widgets appear on your dashboard
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetDashboardLayout}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
                <Button onClick={saveDashboardLayout}>
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboardLayout.widgets?.map((widget) => (
                  <div
                    key={widget.id}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-opacity ${
                      widget.visible ? "" : "opacity-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      <div>
                        <p className="font-medium">{widget.title}</p>
                        <p className="text-xs text-muted-foreground">{widget.type} · {widget.size}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleWidgetVisibility(widget.id)}
                    >
                      {widget.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Language & Region
              </CardTitle>
              <CardDescription>
                Set your preferred language for the interface
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="max-w-sm">
                <Label>Display Language</Label>
                <Select value={currentLanguage} onValueChange={changeLanguage}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(languages).map(([code, name]) => (
                      <SelectItem key={code} value={code}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground mt-2">
                  This will change the language of the user interface
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rate Limits Tab */}
        <TabsContent value="rate-limits">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gauge className="h-5 w-5" />
                API Rate Limits
              </CardTitle>
              <CardDescription>
                Configure request rate limits by role and endpoint
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-4">By Role</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Role</TableHead>
                      <TableHead>Requests/Minute</TableHead>
                      <TableHead>Requests/Hour</TableHead>
                      <TableHead>Burst Limit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(rateLimits.roles || {}).map(([role, config]) => (
                      <TableRow key={role}>
                        <TableCell className="font-medium capitalize">{role}</TableCell>
                        <TableCell>{config.requests_per_minute}</TableCell>
                        <TableCell>{config.requests_per_hour}</TableCell>
                        <TableCell>{config.burst_limit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {Object.keys(rateLimits.endpoints || {}).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-4">By Endpoint</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Endpoint</TableHead>
                        <TableHead>Requests/Minute</TableHead>
                        <TableHead>Requests/Hour</TableHead>
                        <TableHead>Burst Limit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(rateLimits.endpoints || {}).map(([endpoint, config]) => (
                        <TableRow key={endpoint}>
                          <TableCell className="font-mono text-sm">{endpoint}</TableCell>
                          <TableCell>{config.requests_per_minute}</TableCell>
                          <TableCell>{config.requests_per_hour}</TableCell>
                          <TableCell>{config.burst_limit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Adapters Tab — Rubicon/Magnite Credentials */}
        <TabsContent value="adapters">
          <AdaptersSettings api={api} isAdmin={isAdmin} />
        </TabsContent>

        {/* Platform Tab — Storage Settings */}
        {isSuperAdmin && (
          <TabsContent value="platform">
            <Card>
              <CardHeader>
                <CardTitle>Data Storage Settings</CardTitle>
                <CardDescription>Control what data gets stored in the database</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Store Bid Logs</p>
                    <p className="text-sm text-muted-foreground">Store all incoming bid requests in database. Disabling saves storage but removes request-level analytics.</p>
                  </div>
                  <Switch
                    checked={platformSettings.store_bid_logs}
                    onCheckedChange={async (v) => {
                      try {
                        await api.put("/settings/platform", { store_bid_logs: v });
                        setPlatformSettings(prev => ({ ...prev, store_bid_logs: v }));
                        toast.success(`Bid logs storage ${v ? "enabled" : "disabled"}`);
                      } catch { toast.error("Failed to update"); }
                    }}
                    data-testid="store-bid-logs-toggle"
                  />
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Store Audience Data</p>
                    <p className="text-sm text-muted-foreground">Store device/audience data from bid requests. Disabling saves storage but removes audience analytics.</p>
                  </div>
                  <Switch
                    checked={platformSettings.store_audience}
                    onCheckedChange={async (v) => {
                      try {
                        await api.put("/settings/platform", { store_audience: v });
                        setPlatformSettings(prev => ({ ...prev, store_audience: v }));
                        toast.success(`Audience storage ${v ? "enabled" : "disabled"}`);
                      } catch { toast.error("Failed to update"); }
                    }}
                    data-testid="store-audience-toggle"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Note: Bid responses and impressions are always stored regardless of these settings (required for billing and reports).</p>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Permission Set Dialog */}
      <Dialog open={showPermissionDialog} onOpenChange={setShowPermissionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPermissionSet?.id ? (editingPermissionSet.is_system ? "View" : "Edit") : "Create"} Permission Set
            </DialogTitle>
            <DialogDescription>
              Define granular permissions for user roles
            </DialogDescription>
          </DialogHeader>
          <PermissionSetEditor
            permissionSet={editingPermissionSet}
            availablePermissions={availablePermissions}
            onSave={savePermissionSet}
            onCancel={() => setShowPermissionDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Template Editor Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Email Template: {editingTemplate?.name}</DialogTitle>
            <DialogDescription>
              Customize the email template content and subject
            </DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <EmailTemplateEditor
              template={editingTemplate}
              onSave={saveEmailTemplate}
              onCancel={() => setShowTemplateDialog(false)}
              onPreview={previewEmailTemplate}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewHtml} onOpenChange={() => setPreviewHtml(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div
            className="border rounded-lg p-4 bg-white text-black overflow-auto max-h-[60vh]"
            dangerouslySetInnerHTML={{ __html: previewHtml || "" }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
